/**
 * Estado con semaforo (widget `badge`).
 *
 * ARQUITECTURA.md §7 lo pide explicito: "estados con semaforo, no dropdowns de
 * 9 opciones". Con pocas opciones se dibuja un grupo de radios visible — se ven
 * todas las alternativas y cual esta activa sin abrir nada. Cuando son muchas,
 * un grupo de radios ocupa media pantalla y ahi si conviene la lista cerrada.
 *
 * Teclado: es un `radiogroup` real, asi que las flechas mueven entre opciones y
 * el grupo entero ocupa una sola parada de tabulacion (patron APG).
 */

import clsx from 'clsx';
import { useRef } from 'react';

import { ReadonlyOutput, stateTone } from '@/components/fields/FieldDisplay';
import { SelectionField } from '@/components/fields/SelectionField';
import type { FieldInputProps } from '@/components/fields/types';
import type { JsonValue } from '@/lib/types';

/** Mas opciones que esto y el semaforo deja de leerse de un vistazo. */
const MAXIMO_VISIBLE = 5;

const CLASES_TONO: Record<string, string> = {
  neutral: 'border-line-strong bg-surface-raised text-fg',
  accent: 'border-accent bg-accent text-accent-contrast',
  success: 'border-success bg-success-soft text-success',
  warning: 'border-warning bg-warning-soft text-warning',
  danger: 'border-danger bg-danger-soft text-danger',
};

export function StatusField(props: FieldInputProps) {
  const { id, meta, value, onChange, readonly, invalid, describedBy } = props;
  const contenedor = useRef<HTMLDivElement>(null);
  const opciones = meta.choices ?? [];

  if (readonly || opciones.length === 0) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  if (opciones.length > MAXIMO_VISIBLE) {
    return <SelectionField {...props} />;
  }

  const mover = (delta: number): void => {
    const actual = opciones.findIndex((opcion) => opcion.value === value);
    const siguiente = (actual + delta + opciones.length) % opciones.length;
    const destino = opciones[siguiente];
    if (destino === undefined) return;
    onChange(destino.value);
    // El foco sigue a la seleccion, como manda el patron de radiogroup.
    contenedor.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[siguiente]?.focus();
  };

  return (
    <div
      ref={contenedor}
      id={id}
      role="radiogroup"
      aria-labelledby={`${id}-label`}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="flex flex-wrap gap-1.5"
      onKeyDown={(evento) => {
        if (evento.key === 'ArrowRight' || evento.key === 'ArrowDown') {
          evento.preventDefault();
          mover(1);
        } else if (evento.key === 'ArrowLeft' || evento.key === 'ArrowUp') {
          evento.preventDefault();
          mover(-1);
        }
      }}
    >
      {opciones.map((opcion) => {
        const activa = opcion.value === value;
        const tono = stateTone(meta, opcion.value as JsonValue);
        return (
          <button
            key={String(opcion.value)}
            type="button"
            role="radio"
            aria-checked={activa}
            tabIndex={activa ? 0 : -1}
            onClick={() => {
              onChange(opcion.value);
            }}
            className={clsx(
              'touch-target inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-sm font-medium transition-colors',
              activa
                ? CLASES_TONO[tono] ?? CLASES_TONO.neutral
                : 'border-line bg-surface text-fg-muted hover:border-line-strong hover:text-fg',
            )}
          >
            {opcion.label}
          </button>
        );
      })}
    </div>
  );
}
